import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { grey, grey10, red4 } from 'app/components/ThemeProvider/colors';
import BinIcon from 'app/components/Icons/BinIcon';
import EditNameIcon from 'app/components/Icons/EditNameIcon';
import UserAddIcon from 'app/components/Icons/UserAddIcon';
import { Props } from 'app/constants/types';
import EmployeeModal from 'app/components/EmployeeModal';
import CustomInput from 'app/components/CustomInput';
import CloseIcon from 'app/components/Icons/CloseIcon';
import CheckCircle from 'app/components/Icons/CheckCircle';
import { getTheme } from 'app/components/ThemeProvider/theme';
import { PaymentList } from 'app/utils/services/types/salary.type';
import { useUpdatePaymentAmount } from 'app/utils/services/salaryServices';
import AlertTriangleIcon from 'app/components/Icons/AlertTriangleIcon';

import ClearModal, { DeleteModal } from '../ClearModal';

import {
  bottomBtn,
  detailsBtns,
  infoBox,
  mainText,
  menuItems,
  rowBtn,
  secondaryText,
  tableCell,
  tableCellText,
  tableContainer,
  tableHeaderCell,
  tableRowStyle,
} from './SalaryTable.sx';

interface SalaryTableRows extends PaymentList {
  isEditing: boolean;
}

const SalaryTable = (props: Props) => {
  const {
    tableHeader,
    tableRow,
    refetchSalaryData,
    details,
    anchorEl,
    activeAnchor,
    totalAmount,
    handleClose,
    setTotalAmount,
  } = props;
  const theme = getTheme('light');
  const mediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const updateAmount = useUpdatePaymentAmount();

  const [employeeModal, setEmployeeModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [clearModal, setClearModal] = useState(false);
  const [userId, setUserId] = useState<string>('');

  const [tableRows, setTableRows] = useState<SalaryTableRows[]>(() => {
    return tableRow?.map((row) => {
      return {
        ...row,
        isEditing: false,
      };
    });
  });

  const handleChangeSelectedFieldAmount = (value: string, id: string) => {
    setTableRows((prevState) =>
      prevState.map((row) => {
        if (row.id === id) {
          return {
            ...row,
            amount: value,
          };
        }

        return row;
      })
    );
  };

  const changeEditingStatus = (id: string, state: boolean) => {
    setTableRows((prevState) =>
      prevState.map((row) => {
        if (row.id === id) {
          return {
            ...row,
            isEditing: state,
          };
        }

        return row;
      })
    );
  };

  const handleUpdateAmount = (id: string, selectedAmount: string) => {
    updateAmount.mutate(
      { id, amount: selectedAmount },
      {
        onSuccess: (updateAmountData) => {
          handleChangeSelectedFieldAmount(selectedAmount, id);
          changeEditingStatus(id, false);
          setTotalAmount(updateAmountData?.payment_amount);
        },
      }
    );
  };

  const workersCount = tableRows?.length;

  return (
    <TableContainer sx={tableContainer(tableRow?.length === 0)}>
      <Table>
        <TableHead>
          {!!tableRow?.length && (
            <TableRow sx={tableRowStyle}>
              {tableHeader.map((item, index) => (
                <TableCell key={item.id} sx={{ ...tableHeaderCell }}>
                  <Typography sx={{ fontSize: '15px', color: grey, lineHeight: '1' }}>{item.name}</Typography>
                </TableCell>
              ))}
              <TableCell sx={{ ...tableCell }}>
                <Button
                  style={{ minWidth: 'fit-content', padding: 0, justifyContent: 'end' }}
                  variant={'text'}
                  sx={{ ...rowBtn }}
                  onClick={() => {
                    setClearModal(true);
                  }}
                >
                  <Typography component="span" sx={{ fontSize: '15px', color: grey, lineHeight: '1' }}>
                    Hamısını sil
                  </Typography>
                  <BinIcon />
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {tableRows?.map((row, index) => {
            return (
              <TableRow
                key={row?.customer_no}
                sx={
                  row?.iban_error || +row?.amount === 0
                    ? { ...tableRowStyle, background: red4 }
                    : (index + 1) % 2 !== 0
                    ? { ...tableRowStyle, background: grey10 }
                    : tableRowStyle
                }
              >
                <TableCell sx={{ ...tableCell }}>
                  <Typography sx={tableCellText}>{row.full_name}</Typography>
                </TableCell>
                <TableCell sx={{ ...tableCell }}>
                  <Typography sx={tableCellText}>{row.iban}</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    ...tableCell,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'start',
                    gap: '12px',
                  }}
                >
                  {!row.isEditing ? (
                    <Typography sx={tableCellText}>{row.amount} ₼</Typography>
                  ) : (
                    <CustomInput
                      sx={{ height: '36px' }}
                      rest={{ defaultValue: row.amount }}
                      handleChange={(e) => {
                        if (e.target.value.match(/[^0-9.]/)) {
                          e.preventDefault();

                          return;
                        }
                        setAmount(e.target.value);
                      }}
                      value={amount}
                    />
                  )}
                  {!row.isEditing ? (
                    <Button
                      style={{ minWidth: 'fit-content', padding: 0 }}
                      variant={'text'}
                      sx={{
                        ...rowBtn,
                        '& .MuiSvgIcon-root': {
                          width: '16px',
                          height: '16px',
                        },
                      }}
                      onClick={() => {
                        setAmount(row.amount);
                        changeEditingStatus(row.id, true);
                      }}
                    >
                      <EditNameIcon id={'editIcon'} />
                    </Button>
                  ) : (
                    row.isEditing && (
                      <Box sx={{ marginTop: '11px', display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                        <Button
                          disabled={amount === '' || +amount <= 0}
                          sx={detailsBtns('green')}
                          onClick={() => handleUpdateAmount(row.id, amount)}
                        >
                          <CheckCircle color={'secondary'} />
                        </Button>
                        <Button onClick={() => changeEditingStatus(row.id, false)} sx={detailsBtns('red')}>
                          <CloseIcon color={'warning'} />
                        </Button>
                      </Box>
                    )
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableCell,
                    flexDirection: 'row',
                    gap: '20px',
                    alignItems: 'center',
                    justifyContent: 'end',
                  }}
                >
                  {(!!row?.iban_error || +row?.amount === 0) && (
                    <Tooltip title={+row?.amount === 0 ? 'Məbləğ 0 ola bilməz' : row?.iban_error}>
                      <IconButton>
                        <AlertTriangleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Button
                    style={{ minWidth: 'fit-content', padding: 0 }}
                    variant={'text'}
                    sx={{ ...rowBtn, ml: '25px' }}
                    onClick={() => {
                      setUserId(row.id);
                      setDeleteModal(true);
                    }}
                  >
                    <BinIcon />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          <Menu
            id={activeAnchor}
            disableAutoFocusItem
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{ '& .MuiPaper-root': { right: '30px', left: 'auto !important' } }}
          >
            {details.map((detail, index) => (
              <MenuItem
                key={detail.title}
                sx={
                  index + 1 === props.details.length
                    ? { ...menuItems, color: '#FF5F57', '&:hover': { color: '#FF5F57', background: '#F3F4F6' } }
                    : menuItems
                }
              >
                {detail.icon} {detail.title}
              </MenuItem>
            ))}
          </Menu>
        </TableBody>
      </Table>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: !mediumScreen ? '0 32px' : '0 20px',
          gap: '16px',
          marginTop: '1rem',
        }}
      >
        <Button
          onClick={() => {
            setEmployeeModal(true);
          }}
          sx={bottomBtn}
          color={'secondary'}
        >
          <UserAddIcon />
          İşçi əlavə et
        </Button>
        <Box sx={infoBox}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
            <Typography sx={secondaryText}>Ümumi məbləğ:</Typography>
            <Typography sx={mainText}>{totalAmount} ₼</Typography>
          </Box>
          <Box>
            <Divider orientation={'vertical'} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
            <Typography sx={secondaryText}>Seçilmiş əməkdaş sayı:</Typography>
            <Typography sx={mainText}> {workersCount} </Typography>
          </Box>
        </Box>
      </Box>
      <EmployeeModal
        open={employeeModal}
        close={setEmployeeModal}
        refetchSalaryData={refetchSalaryData}
        headerText={'Əməkdaş əlavə et'}
      />
      <DeleteModal
        open={deleteModal}
        close={setDeleteModal}
        refetchSalaryData={refetchSalaryData}
        userId={userId}
        headerText={'Əlavə edilmiş əməkdaşı silmək istəyirsiniz?'}
      />
      <ClearModal
        open={clearModal}
        close={setClearModal}
        refetchSalaryData={refetchSalaryData}
        headerText="Bütün işçiləri siyahıdan silmək istədiyinizdən əminsinizmi?"
      />
    </TableContainer>
  );
};

export default SalaryTable;
